import { Controller } from "@hotwired/stimulus"
import intlTelInput from 'intl-tel-input';
import { de } from "intl-tel-input/i18n";

export default class extends Controller {
  static targets = ["input", "countryCode", "completePhoneNumber"]
  static values = {
    initialCountry: { type: String, default: "de" },
    separateDialCode: { type: Boolean, default: true },
    strictMode: { type: Boolean, default: true },
    countryCodes: { type: Array, default: [
      "de", // Germany
      "at", // Austria
      "be", // Belgium
      "ch", // Switzerland
      "cz", // Czech Republic
      "dk", // Denmark
      "fr", // France
      "hu", // Hungary
      "it", // Italy
      "li", // Liechtenstein
      "lu", // Luxembourg
      "nl", // Netherlands
      "pl", // Poland
      "pt", // Portugal
      "si", // Slovenia
      "sk"  // Slovakia
    ]}
  }

  connect() {
    // Use instance variable to track initialization state
    // This prevents race conditions better than DOM attributes
    if (!this.isInitializing && !this.iti) {
      this.isInitializing = true;
      this.initializePhoneInput();
      this.setupTurboEventHandlers();
    }
  }

  disconnect() {
    if (this.iti) {
      this.iti.destroy();
      this.iti = null;
    }
    this.isInitializing = false;
    this.removeTurboEventHandlers();
  }

  initializePhoneInput() {
    if (!this.hasInputTarget || this.iti) return;

    const initialCountry = this.hasCountryCodeTarget && this.countryCodeTarget.value
      ? this.countryCodeTarget.value
      : this.initialCountryValue;

    this.iti = intlTelInput(this.inputTarget, {
      separateDialCode: this.separateDialCodeValue,
      initialCountry: initialCountry,
      i18n: de,
      strictMode: this.strictModeValue,
      onlyCountries: this.countryCodesValue,
      loadUtils: () => import("intl-tel-input/utils")
    })


    const updateCompletePhoneNumber = () => {
      if (this.hasCompletePhoneNumberTarget) {
      this.completePhoneNumberTarget.value = this.iti.getNumber();
      }
    };
    this.inputTarget.addEventListener('input', updateCompletePhoneNumber);
    this.inputTarget.addEventListener('countrychange', updateCompletePhoneNumber);

    updateCompletePhoneNumber();

    this.setupCountryChangeHandler();
    this.isInitializing = false;
  }

  setupTurboEventHandlers() {
    this.handleTurboBeforeCache = this.handleTurboBeforeCache.bind(this)
    this.handleTurboRender = this.handleTurboRender.bind(this)

    document.addEventListener('turbo:before-cache', this.handleTurboBeforeCache)
    document.addEventListener('turbo:render', this.handleTurboRender)
  }

  removeTurboEventHandlers() {
    document.removeEventListener('turbo:before-cache', this.handleTurboBeforeCache)
    document.removeEventListener('turbo:render', this.handleTurboRender)
  }

  handleTurboBeforeCache() {
    if (this.iti) {
      this.iti.destroy();
      this.iti = null;
    }
    this.isInitializing = false;
  }

  handleTurboRender() {
    if (!this.isInitializing && !this.iti && this.hasInputTarget) {
      this.isInitializing = true;
      this.initializePhoneInput();
    }
  }

  setupCountryChangeHandler() {
    this.inputTarget.addEventListener('countrychange', () => {
      if (this.hasCountryCodeTarget) {
        const countryData = this.iti.getSelectedCountryData()
        this.countryCodeTarget.value = countryData.iso2
      }
    })
  }


  validateAndSubmit(event) {
    this.inputTarget.classList.remove('error')
    this.removeExistingErrorHints()

    if (this.iti && this.iti.isValidNumber()) {
      const phoneNumber = this.iti.getNumber().slice(this.iti.getSelectedCountryData().dialCode.length + 1)
      this.inputTarget.value = phoneNumber

    } else {
      event.preventDefault()
      this.showValidationError()
      // For Turbo form submissions, we need to stop the turbo submission
      if (event.detail && event.detail.formSubmission) {
        event.detail.formSubmission.stop()
      }
    }
  }

  errorContainer() {
    return this.inputTarget.closest('.form-group') || this.element
  }

  removeExistingErrorHints() {
    const existingHint = this.errorContainer().querySelector('.form-hint.error')
    if (existingHint) {
      existingHint.remove()
    }
  }

  showValidationError() {
    this.inputTarget.classList.add('error')

    const hintElement = document.createElement('p')
    hintElement.className = 'form-hint error'
    hintElement.innerHTML = '&nbsp;Bitte geben Sie eine gültige Telefonnummer ein'

    this.errorContainer().appendChild(hintElement)
  }
}